import apiService from './api';

// Subscription statuses
export const SUBSCRIPTION_STATUS = {
  NONE: 'none',
  TRIAL: 'trial',
  TRIAL_EXPIRED: 'trial_expired',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  SUSPENDED: 'suspended',
  CANCELLED: 'cancelled',
  PENDING_DELETION: 'pending_deletion',
  LIFETIME: 'lifetime',
  TRANSFER_TRIAL: 'transfer_trial',
} as const;

export type SubscriptionStatus = typeof SUBSCRIPTION_STATUS[keyof typeof SUBSCRIPTION_STATUS];

export interface SubscriptionTier {
  id: number;
  tier_code: string;
  tier_name: string;
  description: string;
  features: string[];
  price_monthly: string;
  price_annual: string;
  is_trial_tier: boolean;
  is_active: boolean;
  is_legacy: boolean;
  is_special: boolean;
  is_contact_sales: boolean;
base_named_users: number;
  base_public_view_users: number;
  base_api_endpoints: number | null;
  base_scheduled_jobs: number | null;
  base_custom_domains: number | null;
  base_branding_policies: number | null;
  base_odbc_tier1_drivers: number | null;  
  audit_log_retention_days: number | null;
  support_level: string;
  is_popular: boolean;
  icon_name: string | null;
short_description: string | null;
  base_license_features: Record<string, boolean>;
  trial_duration_days: number;
  sort_order: number;
  chargebee_plan_id_monthly: string | null;
  chargebee_plan_id_annual: string | null;
}

export interface SubscriptionDetails {
  workspace_id: number;
  status: SubscriptionStatus;
  tier: SubscriptionTier | null;
  billing_cycle: 'monthly' | 'annual' | null;
  is_lifetime: boolean;
trial: {
    ends_at: string | null;
    days_remaining: number | null;
    is_expired: boolean;
    can_self_extend: boolean;
    extension_days?: number;
    self_extended_at: string | null;
    extension_count: number;
    show_extend_button: boolean;
  } | null;
  current_period: {
    start: string | null;
    end: string | null;
  } | null;
  cancelled_at: string | null;
  scheduled_deletion_at: string | null;
  effective_license: {
    named_users: number;
    public_view_users: number;
    concurrent_users: number;
    mau_users: number;
    api_endpoints: number;
    scheduled_jobs: number;
    custom_domains: number;
    branding_policies: number;
    odbc_tier1_drivers: number;
    features: Record<string, boolean>;
  };
addons: Array<{
    id: number;
    addon_id: number;
    addon_code: string;
    addon_name: string;
    quantity: number;
    price: string;
  }>;
  billing_profile: {
    id: number;
    name: string;
  } | null;
  has_payment_method: boolean;
  chargebee_subscription_id: string | null;
promo_code_used: string | null;
  parity_discount_pct?: number | null;
  parity_coupon_code?: string | null;
  parity_country?: string | null;
chargebee_data?: {
    mrr: number | null;
    coupons: Array<{ coupon_id: string; applied_count: number }>;
    plan_amount: number | null;
  } | null;
}

export interface PreviewResult {
  immediate_charge: number;
  credit_applied: number;
  new_monthly_amount: number;
  new_annual_amount: number;
  effective_date: string;
  is_upgrade: boolean;
  proration_details: string;
}

export interface EstimateLineItem {
  description: string;
  amount: number;
  quantity: number;
  unit_amount: number;
  type: string;
}

export interface EstimateResult {
  tier: {
    id: number;
    name: string;
    code: string;
  };
  billing_cycle: 'monthly' | 'annual';
  line_items: EstimateLineItem[];
  discounts: Array<{ description: string; amount: number }>;
  subtotal: number;
  total: number;
  amount_due: number;
  credits_applied: number;
  currency: string;
  next_billing_at: string | null;
  current_period_end: string | null;
recurring_total: number;
  recurring_line_items: EstimateLineItem[];
  taxes?: Array<{ name?: string; description?: string; amount: number }>;
  tax_amount?: number;
  tax_rate?: number;
  recurring_tax?: number;
  recurring_total_with_tax?: number;
}


export interface SubscriptionAddon {
  id: number;
  addon_code: string;
  addon_name: string;
  addon_type: 'user_allocation' | 'license_feature';
  description: string;
  affects_field: string;
  quantity_per_unit: number;
is_stackable: boolean;
  license_config_type?: 'boolean' | 'quantity' | null;
  applicable_tier_ids: number[];
  pricing: Array<{
    id: number;
    price_monthly: string;
    price_annual: string;
  }>;
}

export interface TierPreview {
  tier_id: number;
  tier_code: string;
  tier_name: string;
  named_users: number;
  public_view_users: number;
  api_endpoints: number;
  scheduled_jobs: number;
  custom_domains: number;
  branding_policies: number;
  odbc_tier1_drivers: number;
  features: Record<string, any>;
  includes_lifetime_bonus: boolean;
  lifetime_bonus?: {
    named_users: number;
    public_view_users: number;
    api_endpoints: number;
    scheduled_jobs: number;
    custom_domains: number;
    branding_policies: number;
    odbc_tier1_drivers: number;
  };
}

export interface LifetimeDeal {
  deal_code: string;
  deal_name: string;
  deal_source: string;
  tier: SubscriptionTier;
  base_named_users: number;
  base_public_view_users: number;
  features: Record<string, boolean>;
  is_valid: boolean;
  validation_error?: string;
}

export interface SubscriptionEvent {
  id: number;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  from_tier_id: number | null;
  to_tier_id: number | null;
  triggered_by: string;
  details: Record<string, any>;
  created_at: string;
}

// Tier access code types
export interface TierAccessCode {
  id: number;
  code: string;
  code_name: string | null;
  description: string | null;
  tier: SubscriptionTier;
}

export interface AvailableTiersResult {
  tiers: (SubscriptionTier & { 
    is_unlocked?: boolean; 
    is_preselected?: boolean;
    is_current?: boolean;
    is_legacy?: boolean;
  })[];
  access_code: { code: string; code_name: string | null } | null;
}

export interface LifetimeStackingSummary {
  workspace_id: number;
  is_lifetime: boolean;
  current_tier: SubscriptionTier | null;
  codes_redeemed: number;
  total_named_users: number;
  total_public_view_users: number;
  features: Record<string, any>;
  codes: Array<{
    code: string;
    source: string;
    granted_named_users: number;
    granted_public_view_users: number;
    redeemed_at: string;
  }>;
  next_tier: {
    tier: SubscriptionTier;
    requires: {
      codes: number;
      named_users: number;
      public_view_users: number;
    };
    progress: {
      codes: number;
      named_users: number;
      public_view_users: number;
    };
  } | null;
}

export interface LifetimeCodeValidation {
  valid: boolean;
  error?: string;
  deal?: {
    id: number;
    code: string;
    source: string;
    name: string;
    tier: SubscriptionTier;
    grants: {
      named_users: number;
      public_view_users: number;
      features: Record<string, any>;
    };
  };
}

export interface StackCodeResult {
  success: boolean;
  workspace: any;
  stacking: {
    codes_redeemed: number;
    total_named_users: number;
    total_public_view_users: number;
    current_tier: string;
    features: Record<string, any>;
  };
}

const CANCELLATION_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'switching_competitor', label: 'Switching to another product' },
  { value: 'not_using', label: 'Not using it enough' },
  { value: 'temporary', label: 'Temporary - will be back' },
  { value: 'technical_issues', label: 'Technical issues / bugs' },
  { value: 'support_issues', label: 'Support experience' },
  { value: 'just_testing', label: 'Just testing / evaluating' },
  { value: 'other', label: 'Other' },
];

class SubscriptionApi {
  // Get subscription details
  async getSubscription(workspaceId: number): Promise<SubscriptionDetails> {
    const response = await apiService.get(`/subscriptions/${workspaceId}`);
    return response.data.subscription;
  }

  // Start trial
  async startTrial(workspaceId: number, tierId: number, trialDays: number = 7): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/start-trial`, {
      tier_id: tierId,
      trial_days: trialDays,
    });
    return response.data;
  }

// Extend trial (self-service, one-time only)
  async extendTrial(workspaceId: number): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/extend-trial`, {});
    return response.data;
  }

async convertTrial(
    workspaceId: number,
options: {
      tier_id?: number;
      billing_cycle?: 'monthly' | 'annual';
      promo_code?: string;
addons?: Array<{ addon_code: string; quantity: number }>;
      payment_intent_id?: string;
      idempotency_key?: string;
    } = {}
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/convert`, options);
    return response.data;
  }

  // Create subscription (non-trial)
async createSubscription(
    workspaceId: number,
    options: {
      tier_id: number;
      billing_cycle: 'monthly' | 'annual';
      promo_code?: string;
addons?: { addon_code: string; quantity: number }[];
      payment_intent_id?: string;
      idempotency_key?: string;
    }
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/create`, options);
    return response.data;
  }
  async createSubscriptionPaymentIntent(
    workspaceId: number,
    options: {
      tier_id: number;
      billing_cycle?: 'monthly' | 'annual';
      promo_code?: string;
      addons?: Array<{ addon_code: string; quantity: number }>;
    }
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/payment-intent`, options);
    return response.data?.intent;
  }

  // Change tier
async changeTier(
    workspaceId: number,
    newTierId: number,
options: {
      billing_cycle?: 'monthly' | 'annual';
      immediate?: boolean;
addons?: Array<{ addon_code: string; quantity: number }>;
      promo_code?: string;
      payment_intent_id?: string;
      idempotency_key?: string;
    } = {}
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/change-tier`, {
      new_tier_id: newTierId,
      ...options,
    });
    return response.data;
  }

  // Preview tier/billing change
  async previewChange(
    workspaceId: number,
    options: {
      new_tier_id?: number;
      billing_cycle?: 'monthly' | 'annual';
    }
  ): Promise<PreviewResult> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/preview`, options);
    return response.data.preview;
  }

  // Get estimate for tier + addons
  async getEstimate(
    workspaceId: number,
    options: {
      tier_id: number;
      billing_cycle: 'monthly' | 'annual';
      addons?: Array<{ addon_code: string; quantity?: number }>;
      promo_code?: string;
    }
  ): Promise<EstimateResult> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/estimate`, options);
return response.data.estimate;
  }

  // Preview tier license (shows combined tier + lifetime bonus for lifetime users)
  async previewTierLicense(workspaceId: number, tierId: number): Promise<TierPreview> {
    const response = await apiService.get(`/subscriptions/${workspaceId}/preview-tier/${tierId}`);
    return response.data;
  }

  // Get available addons for a tier
  async getAddonsForTier(tierId: number): Promise<SubscriptionAddon[]> {
    const response = await apiService.get(`/subscription-tiers/${tierId}/addons`);
    return response.data.addons || [];
  }

  // Cancel subscription
  async cancelSubscription(
    workspaceId: number,
    options: {
      reason: string;
      reason_details?: string;
      immediate?: boolean;
    }
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/cancel`, options);
    return response.data;
  }

  // Reactivate subscription
  async reactivateSubscription(workspaceId: number): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/reactivate`, {});
    return response.data;
  }

  // Add addon
  async addAddon(
    workspaceId: number,
    addonId: number,
    quantity: number = 1
  ): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/addons`, {
      addon_id: addonId,
      quantity,
    });
    return response.data;
  }

  // Remove addon
  async removeAddon(workspaceId: number, addonId: number): Promise<any> {
    const response = await apiService.delete(`/subscriptions/${workspaceId}/addons/${addonId}`);
    return response.data;
  }


  // Get subscription events/history
  async getEvents(workspaceId: number, limit: number = 50): Promise<SubscriptionEvent[]> {
    const response = await apiService.get(`/subscriptions/${workspaceId}/events?limit=${limit}`);
    return response.data.events;
  }

  // Get available tiers
  async getTiers(filterCodes?: string[]): Promise<SubscriptionTier[]> {
    const response = await apiService.get('/subscription-tiers');
    let tiers = response.data.tiers || [];
    
    // Filter active, non-trial, non-legacy tiers
    tiers = tiers.filter((tier: SubscriptionTier) => 
      tier.is_active && !tier.is_trial_tier && !tier.is_legacy
    );
    
    if (filterCodes?.length) {
      tiers = tiers.filter((tier: SubscriptionTier) =>
        filterCodes.includes(tier.tier_code.toLowerCase())
      );
    }
    
    return tiers.sort((a: SubscriptionTier, b: SubscriptionTier) => a.sort_order - b.sort_order);
  }

  // Validate promo code
  async validatePromoCode(code: string, tierId?: number): Promise<any> {
    const response = await apiService.post('/billing/coupons/validate', {
      code,
      plan_id: tierId,
    });
    return response.data;
  }

  // Helper: Get cancellation reasons
  getCancellationReasons() {
    return CANCELLATION_REASONS;
  }

  // Helper: Format status for display
  formatStatus(status: SubscriptionStatus): { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } {
const statusMap: Record<SubscriptionStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
      none: { label: 'No Subscription', color: 'default' },
      trial: { label: 'Trial', color: 'info' },
      trial_expired: { label: 'Trial Expired', color: 'error' },
      transfer_trial: { label: 'Transfer Trial', color: 'warning' },
      active: { label: 'Active', color: 'success' },
      past_due: { label: 'Past Due', color: 'warning' },
      suspended: { label: 'Suspended', color: 'error' },
      cancelled: { label: 'Cancelled', color: 'warning' },
      pending_deletion: { label: 'Pending Deletion', color: 'error' },
      lifetime: { label: 'Lifetime', color: 'success' },
    };
    return statusMap[status] || { label: status, color: 'default' };
  }

  // Helper: Check if status requires urgent action
  isUrgentStatus(status: SubscriptionStatus): boolean {
    return ['trial_expired', 'past_due', 'suspended', 'pending_deletion'].includes(status);
  }


  // Get retention offer for cancellation flow
  async getRetentionOffer(workspaceId: number, step: number = 1): Promise<{
    hasOffer: boolean;
    reason?: string;
    offer?: {
      step: number;
      coupon_code: string;
      description: string;
      coupon_details: any;
    };
    hasNextOffer?: boolean;
  }> {
    const response = await apiService.get(`/subscriptions/${workspaceId}/retention-offer?step=${step}`);
    return response.data;
  }

  // Record that retention offer was shown
  async recordRetentionOfferShown(
    workspaceId: number,
    step: number,
    reason?: string,
    reasonDetails?: string
  ): Promise<void> {
    await apiService.post(`/subscriptions/${workspaceId}/retention-offer/shown`, {
      step,
      reason,
      reason_details: reasonDetails,
    });
  }

// Accept retention offer
  async acceptRetentionOffer(workspaceId: number, step: number): Promise<{ success: boolean; coupon_applied: string }> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/retention-offer/accept`, { step });
    return response.data;
  }

  // ==================== TIER ACCESS CODES ====================

  // Validate tier access code
  async validateAccessCode(code: string): Promise<{ valid: boolean; error?: string; accessCode?: TierAccessCode }> {
    const response = await apiService.post('/tier-access/validate', { code });
    return response.data;
  }

  // Save pending access code to user
  async savePendingAccessCode(code: string): Promise<{ saved: boolean; error?: string; tier?: SubscriptionTier }> {
    const response = await apiService.post('/tier-access/save-pending', { code });
    return response.data;
  }

  // Get user's pending access code
  async getPendingAccessCode(): Promise<{ has_pending: boolean; access_code: TierAccessCode | null }> {
    const response = await apiService.get('/tier-access/pending');
    return response.data;
  }

  // Clear pending access code
  async clearPendingAccessCode(): Promise<void> {
    await apiService.delete('/tier-access/pending');
  }

  // Get available tiers (public + unlocked via access code)
  async getAvailableTiers(accessCode?: string): Promise<AvailableTiersResult> {
    const url = accessCode 
      ? `/tier-access/available-tiers?access_code=${encodeURIComponent(accessCode)}`
      : '/tier-access/available-tiers';
    const response = await apiService.get(url);
    return response.data;
  }

  // Get tiers for existing workspace (includes current even if non-public)
  async getTiersForWorkspace(workspaceId: number): Promise<{ tiers: SubscriptionTier[] }> {
    const response = await apiService.get(`/tier-access/workspace/${workspaceId}/tiers`);
    return response.data;
  }

  // ==================== LIFETIME CODE STACKING ====================

  // Validate lifetime code for stacking
  async validateLifetimeCode(code: string): Promise<LifetimeCodeValidation> {
    const response = await apiService.post('/tier-access/lifetime/validate', { code });
    return response.data;
  }

  // Stack a lifetime code onto a workspace
  async stackLifetimeCode(workspaceId: number, code: string): Promise<StackCodeResult> {
    const response = await apiService.post(`/tier-access/lifetime/stack/${workspaceId}`, { code });
    return response.data;
  }

  // Get lifetime stacking summary for a workspace
  async getLifetimeStackingSummary(workspaceId: number): Promise<LifetimeStackingSummary> {
    const response = await apiService.get(`/tier-access/lifetime/summary/${workspaceId}`);
    return response.data.summary;
  }

  // Redeem a lifetime deal code for a workspace
  async redeemLifetimeDeal(workspaceId: number, code: string): Promise<StackCodeResult> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/lifetime/stack`, { code });
    return response.data;
  }

  // Helper: Check if a workspace status can redeem a lifetime deal
canRedeemLifetimeDeal(status: SubscriptionStatus): boolean {
    return ['none', 'trial', 'trial_expired', 'lifetime'].includes(status);
  }

  async listLifetimeAddons(workspaceId: number): Promise<any[]> {
    const response = await apiService.get(`/subscriptions/${workspaceId}/lifetime-addons`);
    return response.data?.addons || [];
  }

  async getMyLifetimeAddons(workspaceId: number): Promise<any[]> {
    const response = await apiService.get(`/subscriptions/${workspaceId}/lifetime-addons/my`);
    return response.data?.addons || [];
  }

async purchaseLifetimeAddon(workspaceId: number, addonId: number, quantity: number, paymentIntentId?: string, idempotencyKey?: string): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/lifetime-addons/purchase`, { addon_id: addonId, quantity, payment_intent_id: paymentIntentId, idempotency_key: idempotencyKey });
    return response.data;
  }

  async createLifetimeAddonPaymentIntent(workspaceId: number, addonId: number, quantity: number): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/lifetime-addons/payment-intent`, { addon_id: addonId, quantity });
    return response.data?.intent;
  }

  async estimateLifetimeAddon(workspaceId: number, addonId: number, quantity: number): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/lifetime-addons/estimate`, { addon_id: addonId, quantity });
    return response.data?.estimate;
  }

  async refundLifetimeAddon(workspaceId: number, workspaceAddonId: number): Promise<any> {
    const response = await apiService.post(`/subscriptions/${workspaceId}/lifetime-addons/${workspaceAddonId}/refund`, {});
    return response.data;
  }

  async getMauUsage(workspaceId: number): Promise<any> {
    const response = await apiService.get(`/workspaces/${workspaceId}/subscription/mau-usage`);
    return response.data?.mau_usage;
  }
}

const subscriptionApi = new SubscriptionApi();
export default subscriptionApi;
